import { FormProvider, useForm, get, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { IconButton, Stack, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Iconify, RHFCheckbox, RHFTextField } from '@app/components';
import { useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { addList } from '../slice';
import { selectAddListState } from '../selector';
import { IAddListSchema, IList } from '../types';

interface Props {
  showListTexfield: boolean;
  setShowListTexfield: React.Dispatch<React.SetStateAction<boolean>>;
  boardId: number;
  listId?: number;
  setEditing?: (value: string | null) => void;
  data?: IList;
  listIndex?: number;
  handleCloseList?: any;
  isLoggedInUser?: boolean;
}
const AddCardAndList = ({
  showListTexfield,
  setShowListTexfield,
  boardId,
  listId,
  setEditing,
  listIndex,
  data,
  handleCloseList,
}: Props) => {
  const theme = useTheme();

  const dispatch = useDispatch();

  const methods = useForm<IAddListSchema>({
    defaultValues: {
      name: get(data, 'name', ''),
      showInTaskOverView: get(data, 'showInTaskOverView', ''),
    },
  });

  const handleClosePopup = useCallback(() => {
    setEditing?.(null);
    methods.reset();
  }, [setEditing]);

  const { loading } = useRequestState({
    stateSelector: selectAddListState,
    onSuccess: handleClosePopup,
    errorShown: true,
  });

  const onSubmit = (form: IAddListSchema) => {
    dispatch(addList({ ...form, boardId, listId, listIndex }));
  };

  useEffect(() => {
    methods.reset({
      name: get(data, 'name', ''),
      showInTaskOverView: false,
    });
  }, [boardId, methods]);

  const name = useWatch({ control: methods.control, name: 'name' });

  return (
    <>
      <Stack
        style={{
          background: showListTexfield ? theme.palette.grey[200] : theme.palette.grey[300],
          borderRadius: '10px',
          height: 'fit-content',
        }}
        px={2}
        minWidth={'270px'}
        maxWidth={'270px'}
        p={showListTexfield ? 2 : 0}
        spacing={2}
      >
        <FormProvider {...methods}>
          {showListTexfield && (
            <Stack gap={1}>
              <RHFTextField
                name="name"
                sx={{ backgroundColor: 'white', borderRadius: '10px' }}
                placeholder="Enter list name"
              />
              <RHFCheckbox
                sx={{
                  '&.Mui-checked': {
                    color: theme.palette.taskboard.raisinBlack,
                  },
                  bottom: '8px',
                }}
                name="showInTaskOverView"
                label={
                  <span style={{ color: theme.palette.grey[500] }}>
                    {Strings.taskBoard.CheckedIfYouWantToShowThisList}
                  </span>
                }
              />
            </Stack>
          )}
        </FormProvider>
        <Stack sx={{ flexDirection: 'row', width: '100%' }}>
          <LoadingButton
            loading={loading}
            variant="contained"
            type="submit"
            disabled={showListTexfield && name?.trim().length === 0}
            sx={{
              width: showListTexfield ? '50%' : '100%',
              backgroundColor: showListTexfield
                ? theme.palette.taskboard.raisinBlack
                : theme.palette.grey[200],
              '&:hover': {
                bgcolor: theme.palette.taskboard.raisinBlack,
                color: theme.palette.error.contrastText,
              },
              color: showListTexfield ? theme.palette.common.white : theme.palette.common.black,
            }}
            onClick={
              showListTexfield
                ? methods.handleSubmit(onSubmit)
                : () => {
                    setEditing?.(null);
                    setShowListTexfield(true);
                  }
            }
          >
            {showListTexfield ? Strings.taskBoard.saveList : Strings.taskBoard.addAnotherList}
          </LoadingButton>
          {showListTexfield && (
            <IconButton
              aria-label="close"
              onClick={handleCloseList}
              sx={{
                color: (theme) => theme.palette.common.black,
                ml: 2,
              }}
            >
              <Iconify icon={'material-symbols:close'} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </>
  );
};

export { AddCardAndList };
